import React, { useState, useEffect, useMemo } from 'react';
import './Wishpop.css';
import axios from 'axios';

function WishPop({ product, onClose }) {
	const [quantity, setQuantity] = useState(1);
	const [selectedOption, setSelectedOption] = useState(null);
	const [selectedSize, setSelectedSize] = useState(null);
	const [selectedImage, setSelectedImage] = useState(0);
	const [displayImage, setDisplayImage] = useState(null);
	const [isAddingToCart, setIsAddingToCart] = useState(false);

	const getImageUrl = (url) => {
		if (!url) return null;
		if (url.startsWith('/media')) {
			return `https://api.weartoday.kr${url}`;
		}
		if (url.startsWith('http')) {
			return url;
		}
		return `https://api.weartoday.kr/media/${url}`;
	};

	useEffect(() => {
		if (!product) return;

		if (product.main_images && product.main_images.length > 0) {
			const imageUrls = product.main_images.filter(url => !url.toLowerCase().endsWith('.mp4'));
			if (imageUrls.length > 0) {
				setDisplayImage(getImageUrl(imageUrls[0]));
				setSelectedImage(0);
			}
		}

		if (product.parsed_options && product.parsed_options.length > 0) {
			const firstOption = product.parsed_options[0];
			const sizesForFirstOption = product.parsed_options
				.filter(o => o.option_name === firstOption.option_name)
				.map(o => ({
					size: o.size,
					price: o.price,
					base_price: o.base_price,
					stock: o.stock
				}));

			setSelectedOption({
				...firstOption,
				sizes: sizesForFirstOption
			});

			if (sizesForFirstOption.length > 0) {
				setSelectedSize(sizesForFirstOption[0].size);
			}
		} else {
			setSelectedOption({
				option_name: '기본',
				price: product.price || 0,
				base_price: product.base_price || 0,
				stock: product.stock || 0,
				sizes: []
			});
		}
	}, [product]);

	const handleOptionChange = (option) => {
		setSelectedOption(option);

		if (option.image) {
			setDisplayImage(getImageUrl(option.image));
		}

		if (option.sizes && option.sizes.length > 0) {
			setSelectedSize(option.sizes[0].size);
		} else {
			setSelectedSize(null);
		}
	};

	const currentPrice = useMemo(() => {
		if (!product || !selectedOption) return 0;

		if (selectedSize && selectedOption.sizes && selectedOption.sizes.length > 0) {
			const sizeOption = selectedOption.sizes.find(s => s.size === selectedSize);
			if (sizeOption) {
				return Number(sizeOption.final_price || sizeOption.price || 0);
			}
		}

		return Number(selectedOption.price || product.price || 0);
	}, [product, selectedOption, selectedSize]);

	const currentBasePrice = useMemo(() => {
		if (!product || !selectedOption) return 0;

		if (selectedSize && selectedOption.sizes && selectedOption.sizes.length > 0) {
			const sizeOption = selectedOption.sizes.find(s => s.size === selectedSize);
			if (sizeOption && sizeOption.base_price) {
				return Number(sizeOption.base_price);
			}
		}

		if (selectedOption.base_price) {
			return Number(selectedOption.base_price);
		}

		return Math.floor((currentPrice / 0.35) / 100) * 100;
	}, [product, selectedOption, selectedSize, currentPrice]);

	const handleAddToCart = async () => {
		if (isAddingToCart) return;
		setIsAddingToCart(true);

		try {
			const token = localStorage.getItem('access_token');

			if (!token) {
				alert('로그인이 필요합니다.');
				onClose();
				return;
			}

			if (product.parsed_options?.length > 0 && !selectedOption) {
				alert('옵션을 선택해주세요.');
				setIsAddingToCart(false);
				return;
			}

			if (selectedOption?.sizes?.length > 0 && !selectedSize) {
				alert('사이즈를 선택해주세요.');
				setIsAddingToCart(false);
				return;
			}

			await axios.post(
				'https://api.weartoday.kr/api/products/cart/',
				{
					product: product.id,
					option_name: selectedOption?.option_name || '',
					size: selectedSize || '',
					quantity: quantity,
					price: currentPrice
				},
				{
					headers: {
						'Authorization': `Bearer ${token}`
					}
				}
			);

			window.dispatchEvent(new Event('cartUpdated'));
			alert('장바구니에 추가되었습니다!');
			onClose();

		} catch (error) {
			console.error('장바구니 추가 실패:', error);
			alert('장바구니 추가에 실패했습니다.');
		} finally {
			setIsAddingToCart(false);
		}
	};

	const getColorForOption = (optionName) => {
		if (!optionName) return null;

		const colorMap = {
			'블랙': '#000000', '화이트': '#FFFFFF', '그레이': '#808080',
			'브라운': '#8B4513', '베이지': '#F5F5DC', '네이비': '#000080',
			'핑크': '#FFC0CB', '레드': '#FF0000', '블루': '#0000FF',
			'그린': '#008000', '민트': '#98FB98', '옐로우': '#FFFF00',
			'퍼플': '#800080', '오렌지': '#FFA500', '카키': '#BDB76B',
			'와인': '#722F37', '아이보리': '#FFFFF0', '스카이블루': '#87CEEB'
		};

		for (const [color, code] of Object.entries(colorMap)) {
			if (optionName.includes(color)) return code;
		}

		return null;
	};

	if (!product) return null;

	const allOptions = product.parsed_options || [];
	const imageUrls = (product.main_images || []).filter(url => !url.toLowerCase().endsWith('.mp4'));

	return (
		<div className="wishpop-overlay" onClick={onClose}>
			<div className="wishpop-content" onClick={(e) => e.stopPropagation()}>
				<button className="wishpop-close" onClick={onClose}>✕</button>

				<div className="wishpop-container">
					<div className="wishpop-image-section">
						{displayImage ? (
							<img src={displayImage} alt={product.name} />
						) : imageUrls.length > 0 ? (
							<img src={getImageUrl(imageUrls[0])} alt={product.name} />
						) : (
							<div className="no-image">이미지 없음</div>
						)}
					</div>

					<div className="wishpop-info-section">
						<h2 className="wishpop-product-name">{product.name}</h2>

						<div className="wishpop-price-section">
							{currentBasePrice > currentPrice && (
								<span className="wishpop-base-price">
									{currentBasePrice.toLocaleString()}원
								</span>
							)}
							<span className="wishpop-price">{currentPrice.toLocaleString()}원</span>
							{currentBasePrice > currentPrice && (
								<span className="wishpop-discount">
									{Math.round((1 - currentPrice / currentBasePrice) * 100)}% 할인
								</span>
							)}
						</div>

						{allOptions.length > 0 && (
							<div className="wishpop-option-section">
								<div className="wishpop-section-title">옵션 선택</div>
								<div className="wishpop-option-grid">
									{[...new Map(allOptions.map(item => [item.option_name, item])).values()]
										.map((option, idx) => {
											const image = product.color_images?.[idx];
											return (
												<div
													key={idx}
													className={`wishpop-option-item ${selectedOption?.option_name === option.option_name ? 'active' : ''}`}
													onClick={() => {
														const sizesForOption = allOptions
															.filter(o => o.option_name === option.option_name)
															.map(o => ({
																size: o.size,
																price: o.price,
																base_price: o.base_price,
																stock: o.stock
															}));

														handleOptionChange({
															...option,
															sizes: sizesForOption,
															image: image?.url
														});
													}}
												>
													{image ? (
														<img src={getImageUrl(image.url)} alt={option.option_name} className="wishpop-option-image" />
													) : (
														<div className="wishpop-option-color" style={{
															background: getColorForOption(option.option_name) || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
														}}>
															{option.option_name.slice(0, 4)}
														</div>
													)}
													<span className="wishpop-option-name">{option.option_name}</span>
													<span className="wishpop-option-price">{Number(option.price).toLocaleString()}원</span>
												</div>
											);
										})}
								</div>
							</div>
						)}

						{selectedOption?.sizes?.length > 0 && (
							<div className="wishpop-option-section">
								<div className="wishpop-section-title">사이즈 선택</div>
								<div className="wishpop-size-options">
									{selectedOption.sizes.map((sizeOption, idx) => (
										<button
											key={idx}
											className={`wishpop-size-option ${selectedSize === sizeOption.size ? 'active' : ''}`}
											onClick={() => setSelectedSize(sizeOption.size)}
										>
											{sizeOption.size}
										</button>
									))}
								</div>
							</div>
						)}

						<div className="wishpop-option-section">
							<div className="wishpop-section-title">수량</div>
							<div className="wishpop-quantity-selector">
								<button onClick={() => setQuantity(Math.max(1, quantity - 1))}>-</button>
								<input
									type="number"
									value={quantity}
									onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
									min="1"
								/>
								<button onClick={() => setQuantity(quantity + 1)}>+</button>
							</div>
						</div>

						<div className="wishpop-total-section">
							<span className="wishpop-total-label">총 상품금액</span>
							<span className="wishpop-total-amount">
								{(currentPrice * quantity).toLocaleString()}원
							</span>
						</div>

						<button
							className="wishpop-btn-cart"
							onClick={handleAddToCart}
							disabled={isAddingToCart}
						>
							{isAddingToCart ? '추가 중...' : '장바구니 담기'}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}

export default WishPop;